import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Upload a file to Firebase Storage and return its download URL and base64 data
 * @param {File} file - The file to upload
 * @param {string} path - The storage path (e.g., 'uploads/userId/filename')
 * @returns {Promise<{url: string, data: string, mimeType: string}>}
 */
export const uploadFileAndPrepareData = async (file, path) => {
    try {
        // 1. Upload to Firebase Storage
        const storageRef = ref(storage, path);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(snapshot.ref);

        // 2. Read as base64 for Gemini inlineData
        const base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

        return {
            url: downloadUrl,
            data: base64Data,
            mimeType: file.type
        };
    } catch (error) {
        console.error('Error in uploadFileAndPrepareData:', error);
        throw error;
    }
};

/**
 * Convert a File object to base64
 * @param {File} file 
 * @returns {Promise<string>} base64 string
 */
export const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

/**
 * Fetch a file from a URL and convert it to base64
 * @param {string} url 
 * @returns {Promise<string>} base64 string
 */
export const urlToBase64 = async (url) => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        return await fileToBase64(blob);
    } catch (error) {
        console.error('Error converting URL to base64:', error);
        throw error;
    }
};
